import { useState, useMemo, useRef, useCallback, useEffect } from 'react'
import { Search, Loader2 } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { useModelGroupsInfinite } from '@/hooks/useStoreModels'
import { cn } from '@/utils/cn'

interface StoreGroupSelectorProps {
  modelId: string
  initialSelectedGroupIds?: string[]
  onSelectionChange: (ids: string[]) => void
  selectedGroupIds: string[]
  disabled?: boolean
}

export function StoreGroupSelector({
  modelId,
  selectedGroupIds,
  onSelectionChange,
  disabled = false,
}: StoreGroupSelectorProps) {
  const [search, setSearch] = useState('')
  const scrollRef = useRef<HTMLDivElement>(null)
  const sentinelRef = useRef<HTMLDivElement>(null)

  const { data, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useModelGroupsInfinite(modelId, search)

  const groups = useMemo(
    () => data?.pages.flatMap(page => page.data) ?? [],
    [data]
  )

  const totalCount = data?.pages[0]?.pagination.total ?? 0

  const selectedSet = useMemo(
    () => new Set(selectedGroupIds),
    [selectedGroupIds]
  )

  const allVisibleSelected =
    groups.length > 0 && groups.every(g => selectedSet.has(g.id))

  // Intersection observer for infinite scroll
  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel) return

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage()
        }
      },
      { root: scrollRef.current, threshold: 0.1 }
    )

    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  const handleToggleGroup = useCallback(
    (groupId: string) => {
      if (selectedSet.has(groupId)) {
        onSelectionChange(selectedGroupIds.filter(id => id !== groupId))
      } else {
        onSelectionChange([...selectedGroupIds, groupId])
      }
    },
    [selectedSet, selectedGroupIds, onSelectionChange]
  )

  const handleToggleAll = () => {
    const visibleIds = groups.map(g => g.id)
    if (allVisibleSelected) {
      // Deselect all visible, keep hidden selections
      const visibleSet = new Set(visibleIds)
      onSelectionChange(selectedGroupIds.filter(id => !visibleSet.has(id)))
    } else {
      // Select all visible, preserve existing
      const merged = new Set([...selectedGroupIds, ...visibleIds])
      onSelectionChange(Array.from(merged))
    }
  }

  return (
    <div className="space-y-3">
      {/* Search bar */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search groups..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* Header with count and Select All */}
      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground">
          {selectedGroupIds.length} of {totalCount} selected
        </span>
        {!disabled && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleToggleAll}
            disabled={groups.length === 0}
            className="text-xs"
          >
            {allVisibleSelected ? 'Deselect All' : 'Select All'}
          </Button>
        )}
      </div>

      {/* Scrollable checkbox list */}
      <div
        ref={scrollRef}
        className="max-h-[400px] overflow-y-auto rounded-md border"
      >
        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        )}

        {!isLoading && groups.length === 0 && (
          <p className="text-sm text-muted-foreground text-center py-6">
            No groups found.
          </p>
        )}

        {!isLoading &&
          groups.map(group => (
            <label
              key={group.id}
              className={cn(
                'flex items-center gap-3 px-3 py-2.5 transition-colors border-b last:border-0',
                !disabled && 'cursor-pointer hover:bg-accent'
              )}
            >
              <Checkbox
                checked={selectedSet.has(group.id)}
                onCheckedChange={() => handleToggleGroup(group.id)}
                disabled={disabled}
                className={cn(!disabled && 'cursor-pointer')}
              />
              <span className="flex-1 text-sm font-medium">{group.name}</span>
              <Badge variant="secondary" className="text-xs">
                {group.member_count}{' '}
                {group.member_count === 1 ? 'store' : 'stores'}
              </Badge>
            </label>
          ))}

        {/* Sentinel for infinite scroll */}
        <div ref={sentinelRef} className="h-1" />

        {isFetchingNextPage && (
          <div className="flex items-center justify-center py-3">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        )}
      </div>
    </div>
  )
}
